
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, clearAllUserData, isValidTokenFormat } from '@/config/api';
import { debugLog, debugError } from '@/config/debug';

interface User {
  id: string;
  email: string;
  username: string;
  confirmed: boolean;
  first_use?: boolean;
  first_name?: string;
  last_name?: string;
  specialty?: string;
  microphone?: string;
  tour?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<{ needsConfirmation: boolean; user: any }>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  loading: boolean;
  updateUser: (userData: Partial<User>) => void;
  getUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    debugLog('HOOK', 'AuthProvider useEffect triggered');
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('mediscribe_user');
    
    debugLog('HOOK', 'Token exists:', !!token);
    debugLog('HOOK', 'Stored user exists:', !!storedUser);
    
    // Validate token format before proceeding
    if (token && !isValidTokenFormat(token)) {
      debugLog('HOOK', '🚨 Invalid token format detected - clearing data');
      clearAllUserData();
      setLoading(false);
      return;
    }
    
    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        debugLog('HOOK', 'Parsed stored user:', userData);
        setUser(userData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchUser();
      } catch (error) {
        debugError('HOOK', 'Error parsing stored user:', error);
        debugLog('HOOK', '🚨 Error parsing stored user - clearing data');
        clearAllUserData();
        setLoading(false);
      }
    } else if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      debugLog('HOOK', 'No token found, setting loading to false');
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      debugLog('HOOK', 'Fetching user from API...');
      const response = await axios.get(`${API_BASE_URL}/users/me`);
      const userData = response.data;
      debugLog('HOOK', 'Fetched user data:', userData);
      setUser(userData);
      
      // Update localStorage with fresh user data
      localStorage.setItem('mediscribe_user', JSON.stringify(userData));
    } catch (error: any) {
      debugError('HOOK', 'Error fetching user:', error);
      
      // Check if it's a network error vs authentication error
      if (error.response?.status === 401 || error.response?.status === 403) {
        debugLog('HOOK', '🚨 Authentication error while fetching user - token invalid');
      } else if (error.code === 'NETWORK_ERROR') {
        debugLog('HOOK', '🌐 Network error while fetching user');
      } else {
        debugLog('HOOK', '🚨 Unexpected error while fetching user - clearing data');
      }
      
      clearAllUserData();
      setUser(null);
    } finally {
      debugLog('HOOK', 'Setting loading to false');
      setLoading(false);
    }
  };

  const login = async (identifier: string, password: string) => {
    try {
      debugLog('HOOK', '🔄 Starting login process...');
      debugLog('HOOK', '📧 Identifier:', identifier);
      
      const response = await axios.post(`${API_BASE_URL}/auth/local`, {
        identifier,
        password,
      });
      
      const { jwt, user } = response.data;
      debugLog('HOOK', '✅ Login successful, user:', user);
      
      // Validate token before storing
      if (!isValidTokenFormat(jwt)) {
        throw new Error('Token inválido recibido del servidor');
      }
      
      localStorage.setItem('token', jwt);
      localStorage.setItem('mediscribe_user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
      setUser(user);
    } catch (error: any) {
      debugError('HOOK', 'Login error:', error);
      throw new Error(error.response?.data?.error?.message || 'Error de autenticación');
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      const name = `${firstName} ${lastName}`;
      const lang = navigator.language.split('-')[0] || 'es';
      
      debugLog('HOOK', '🔄 Starting registration process...');
      debugLog('HOOK', '📧 Email:', email);
      debugLog('HOOK', '👤 Name:', name);
      debugLog('HOOK', '🌍 Language:', lang);
      
      const response = await axios.post(`${API_BASE_URL}/auth/local/register`, {
        username: email,
        email: email,
        password: password,
        name: name,
        lang: lang,
        first_name: firstName,
        last_name: lastName,
      });
      
      const { jwt, user } = response.data;
      debugLog('HOOK', '✅ Registration successful, user:', user);
      debugLog('HOOK', '🔑 JWT received:', !!jwt);
      
      // If JWT is provided, validate and store it
      if (jwt) {
        if (!isValidTokenFormat(jwt)) {
          throw new Error('Token inválido recibido del servidor');
        }
        
        debugLog('HOOK', '🔑 Valid JWT received, storing token and user data');
        localStorage.setItem('token', jwt);
        localStorage.setItem('mediscribe_user', JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
        setUser(user);
        return { needsConfirmation: false, user };
      } else {
        // No JWT means email confirmation is required
        debugLog('HOOK', '📨 No JWT received, email confirmation required');
        return { needsConfirmation: true, user };
      }
    } catch (error: any) {
      debugError('HOOK', 'Registration error:', error);
      throw new Error(error.response?.data?.error?.message || 'Error en el registro');
    }
  };

  const logout = () => {
    debugLog('HOOK', 'Logging out user');
    clearAllUserData();
    setUser(null);
  };

  const forgotPassword = async (email: string) => {
    try {
      debugLog('HOOK', '🔄 Starting forgot password process for:', email);
      await axios.post(`${API_BASE_URL}/auth/forgot-password`, {
        email,
      });
      debugLog('HOOK', '✅ Forgot password email sent successfully');
    } catch (error: any) {
      debugError('HOOK', 'Forgot password error:', error);
      throw new Error(error.response?.data?.error?.message || 'Error al enviar email');
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      debugLog('HOOK', 'Updating user:', updatedUser);
      setUser(updatedUser);
      localStorage.setItem('mediscribe_user', JSON.stringify(updatedUser));
    }
  };

  const getUser = async () => {
    await fetchUser();
  };

  debugLog('HOOK', 'AuthProvider rendering - user:', user, 'loading:', loading);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      logout,
      forgotPassword,
      loading,
      updateUser,
      getUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.error('useAuth must be used within an AuthProvider - context is undefined');
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
